/**
 * Combat core — the turn-based, deterministic, perfect-information fight loop.
 *
 * Responsibilities:
 *  - draw/reshuffle (RNG-driven, deterministic)
 *  - card resolution + targeting + effects
 *  - damage application (status math from status.ts)
 *  - the Trace clock integration (trace.ts)
 *  - enemy AI: choose intent, resolve intents on end-turn
 *  - win / lose detection
 *
 * Purity contract: these functions mutate the CombatState/RunState objects that
 * are passed in. The PUBLIC api layer (api.ts) is responsible for deep-cloning
 * run state before calling these, so external callers get immutable semantics.
 * Within a single reducer call we mutate freely for performance + clarity.
 *
 * RNG: a single Rng is threaded through; its final state is written back to
 * run.rng by the api layer. Never call Math.random here.
 */

import { Rng } from "./rng";
import {
  addStatus,
  computeIncomingDamage,
  computeOutgoingDamage,
  getStacks,
  setStatus,
  tickEndOfTurnStatuses,
} from "./status";
import {
  addTrace,
  isCountdownActive,
  tickHardCountdown,
  TRACE_TUNING,
} from "./trace";
import { getCardDef } from "./content/cards";
import { getIceDef } from "./content/ice";
import {
  aggregateProgramMods,
  fireHook,
  type HookContext,
  type ProgramDeps,
} from "./programs";
import type {
  CardEffect,
  CombatEvent,
  CombatPhase,
  CombatState,
  IceInstance,
  Intent,
  PendingHook,
  ProgramHook,
  RunState,
  StatusKind,
  StatusMap,
  TraceState,
} from "./types";

/**
 * Program-engine bridge: the primitives the (cycle-free) program interpreter
 * needs. Defined once so every hook call shares it.
 */
const PROGRAM_DEPS: ProgramDeps = {
  dealToIce: (run, ice, raw, events) => dealToIce(run, ice, raw, events),
  dealToRunner: (run, raw, unblockable, events) =>
    dealToRunner(run, raw, unblockable, events),
  drawCards: (run, n, rng, events) => drawCards(run.combat!, n, rng, events),
  pushTrace: (run, delta, reason, events) => pushTrace(run, delta, reason, events),
};

/** Fire a program hook with the shared deps + given rng/ctx. */
function fireRunnerHook(
  run: RunState,
  hook: ProgramHook,
  ctx: HookContext,
  rng: Rng,
  events: CombatEvent[],
): void {
  fireHook(run, hook, ctx, rng, PROGRAM_DEPS, events);
}

/**
 * Enqueue a deferred Program hook (enemyKilled / hpLost / traceGained). The
 * damage/trace primitives can't fire hooks directly (no Rng in scope), so they
 * queue here; drainHooks fires them once an Rng is available.
 */
function enqueueHook(run: RunState, pending: PendingHook): void {
  // Fast path: nothing to fire for the program-less runner.
  if (run.programs.length === 0) return;
  if (!run._pendingHooks) run._pendingHooks = [];
  run._pendingHooks.push(pending);
}

/**
 * Drain the pending-hook queue, firing each with the given Rng. Drains in a
 * loop because a hook's effects may enqueue more (e.g. Plague Vector spreading
 * Virus that kills another ICE). FIFO + a hard bound keep it deterministic and
 * non-looping.
 */
function drainHooks(run: RunState, rng: Rng, events: CombatEvent[]): void {
  if (!run._pendingHooks || run._pendingHooks.length === 0) return;
  let guard = 0;
  while (run._pendingHooks && run._pendingHooks.length > 0) {
    if (++guard > 1000) break; // safety: never spin forever
    const pending = run._pendingHooks.shift()!;
    const ctx: HookContext = {};
    if (pending.amount != null) ctx.amount = pending.amount;
    if (pending.targetUid != null && run.combat) {
      ctx.target = run.combat.enemies.find((e) => e.uid === pending.targetUid);
    }
    fireRunnerHook(run, pending.hook, ctx, rng, events);
  }
  run._pendingHooks = [];
}

export const COMBAT_TUNING = {
  handSize: 5,
  cyclesPerTurn: 3,
} as const;

/**
 * Read a combat's phase opaquely. Used so TypeScript doesn't over-narrow the
 * phase literal after we set it: phase can be mutated back to "lost"/"won" by
 * the damage/intent helpers we call in between, and the compiler can't see that
 * through function boundaries.
 */
function phaseOf(combat: CombatState): CombatPhase {
  return combat.phase;
}

// ---------------------------------------------------------------------------
// Construction
// ---------------------------------------------------------------------------

/** Build live ICE instances from encounter defIds, rolling HP via RNG. */
export function spawnEnemies(
  defIds: string[],
  rng: Rng,
  allocUid: () => number,
): IceInstance[] {
  return defIds.map((defId) => makeIce(defId, rng, allocUid));
}

export function makeIce(
  defId: string,
  rng: Rng,
  allocUid: () => number,
): IceInstance {
  const def = getIceDef(defId);
  let hp = def.maxHp;
  if (def.hpMin != null && def.hpMax != null && def.hpMax >= def.hpMin) {
    hp = rng.int(def.hpMin, def.hpMax);
  }
  const ice: IceInstance = {
    uid: allocUid(),
    defId,
    hp,
    maxHp: hp,
    block: 0,
    statuses: def.innateStatus ? { ...def.innateStatus } : {},
    intent: { kind: "unknown" },
    aiCursor: 0,
    alive: true,
  };
  return ice;
}

/**
 * Start a combat: shuffle the run deck into the draw pile, init piles, spawn
 * enemies, choose their first intents, and draw the opening hand.
 * Mutates `run` (sets run.combat and run.trace). Returns events.
 */
export function startCombat(
  run: RunState,
  enemyDefIds: string[],
  rng: Rng,
  trace: TraceState,
): CombatEvent[] {
  const events: CombatEvent[] = [];
  const allocUid = () => run.nextUid++;

  const drawPile = rng.shuffle(run.deck.map((c) => ({ ...c })));
  const enemies = spawnEnemies(enemyDefIds, rng, allocUid);

  // Aggregate static Program mods (cycles/hand/block/trace ceiling + innate).
  const mods = aggregateProgramMods(run);

  // Apply Trace-ceiling / per-turn mods to the trace state for THIS combat.
  trace.max = Math.max(1, trace.max + mods.traceMaxDelta);
  trace.perTurn = Math.max(0, trace.perTurn + mods.tracePerTurnDelta);

  const cyclesPerTurn = COMBAT_TUNING.cyclesPerTurn + mods.cyclesPerTurn;

  const combat: CombatState = {
    phase: "player",
    turn: 0,
    cyclesPerTurn,
    cycles: 0,
    hand: [],
    drawPile,
    discardPile: [],
    exhaustPile: [],
    enemies,
    cardsPlayedThisTurn: 0,
    attacksPlayedThisTurn: 0,
    log: [],
    endedTurn: false,
    programCounters: {},
    programHandBonus: mods.handSize,
  };
  run.combat = combat;
  run.trace = trace;

  // Fresh runner statuses, then seed innate Program statuses + starting block.
  run._runnerStatuses = {};
  for (const [k, v] of Object.entries(mods.innateStatus)) {
    if (v > 0) {
      run._runnerStatuses = addStatus(run._runnerStatuses, k as StatusKind, v);
    }
  }
  if (mods.startingBlock > 0) {
    run._runnerStatuses = addStatus(run._runnerStatuses, "block", mods.startingBlock);
  }

  // Choose initial intents.
  for (const ice of enemies) {
    chooseIntent(ice, combat, trace, rng);
  }

  // Program combatStart hooks fire BEFORE the opening hand is drawn, so relics
  // like Glass Cannon / Viral Load are active turn one.
  fireRunnerHook(run, "combatStart", {}, rng, events);
  drainHooks(run, rng, events);

  // Begin turn 1.
  beginPlayerTurn(run, rng, events);
  return events;
}

// ---------------------------------------------------------------------------
// Drawing
// ---------------------------------------------------------------------------

/** Draw `n` cards, reshuffling discard into draw when needed. */
export function drawCards(
  combat: CombatState,
  n: number,
  rng: Rng,
  events: CombatEvent[],
): void {
  let drawn = 0;
  for (let i = 0; i < n; i++) {
    if (combat.drawPile.length === 0) {
      if (combat.discardPile.length === 0) break; // nothing left to draw
      // Reshuffle discard into draw.
      combat.drawPile = rng.shuffle(combat.discardPile);
      combat.discardPile = [];
      events.push({ t: "reshuffle" });
    }
    const card = combat.drawPile.shift();
    if (!card) break;
    combat.hand.push(card);
    drawn++;
  }
  if (drawn > 0) events.push({ t: "draw", count: drawn });
}

// ---------------------------------------------------------------------------
// Turn lifecycle
// ---------------------------------------------------------------------------

export function beginPlayerTurn(
  run: RunState,
  rng: Rng,
  events: CombatEvent[],
): void {
  const combat = run.combat!;
  combat.turn += 1;
  combat.phase = "player";
  combat.endedTurn = false;
  combat.cardsPlayedThisTurn = 0;
  combat.attacksPlayedThisTurn = 0;

  events.push({ t: "turnStart", turn: combat.turn });

  // Player block clears at the start of their turn (block is a one-turn shield).
  // Runner "block" is tracked as a status on a synthetic actor (the run). We
  // store runner block in combat via a status map on the runner — see runner
  // helpers below.
  runnerStatuses(run); // ensure exists
  run._runnerStatuses = setStatus(run._runnerStatuses, "block", 0);

  // Start-of-turn DoT/regen for the RUNNER.
  applyStartOfTurnStatusTicks(run, run._runnerStatuses!, true, undefined, events);

  // Cycles: base + overclock.
  const overclock = getStacks(run._runnerStatuses, "overclock");
  combat.cycles = combat.cyclesPerTurn + overclock;

  // Draw hand (base + Program hand bonus).
  drawCards(combat, COMBAT_TUNING.handSize + combat.programHandBonus, rng, events);

  // Program start-of-turn triggers (after the draw, so draw-more relics stack).
  fireRunnerHook(run, "turnStart", {}, rng, events);
  drainHooks(run, rng, events);

  checkCombatEnd(run, events);
}

/**
 * End the player's turn: discard hand, resolve enemy intents, tick statuses,
 * advance the Trace clock + hard countdown, then begin the next player turn
 * (unless combat ended).
 */
export function endTurn(run: RunState, rng: Rng, events: CombatEvent[]): void {
  const combat = run.combat!;
  if (combat.phase !== "player") return;

  // Program end-of-turn triggers fire while it's still the player's turn, so
  // they can finish enemies before the enemy phase (e.g. Contagion Engine).
  fireRunnerHook(run, "turnEnd", {}, rng, events);
  drainHooks(run, rng, events);
  checkCombatEnd(run, events);
  if (phaseOf(combat) === "won") return;

  combat.endedTurn = true;
  combat.phase = "enemy";

  // Discard the whole hand.
  for (const c of combat.hand) combat.discardPile.push(c);
  combat.hand = [];

  // Clear each enemy's block at the START of their phase. Block granted by a
  // `defend` intent this round therefore persisted through the player's whole
  // turn (the player had a chance to chip it), and the enemy now re-shields
  // fresh as part of this phase's intents.
  for (const ice of combat.enemies) {
    if (ice.alive) ice.block = 0;
  }

  // Enemy start-of-(enemy)-turn DoT: virus/poison tick on ICE before they act.
  // (DoT can finish the fight — e.g. last ICE dies to poison.)
  applyEnemyStartOfTurnDoT(run, events);
  drainHooks(run, rng, events); // DoT kills can trigger kill relics
  checkCombatEnd(run, events);
  if (phaseOf(combat) === "won") return;

  // End-of-turn duration tick for the RUNNER (weak/vulnerable etc. on runner).
  run._runnerStatuses = tickEndOfTurnStatuses(run._runnerStatuses);

  // Baseline trace gain on ending a turn + any per-enemy passive trace.
  let traceGain = run.trace!.perTurn;
  for (const ice of combat.enemies) {
    if (!ice.alive) continue;
    const def = getIceDef(ice.defId);
    if (def.tracePerTurn) traceGain += def.tracePerTurn;
  }
  pushTrace(run, traceGain, "end-turn", events);
  drainHooks(run, rng, events); // traceGained relics (e.g. Proxy Chain)
  checkCombatEnd(run, events);
  if (phaseOf(combat) === "won") return;

  // Resolve each living enemy's intent in order. Any hit may kill the runner.
  for (const ice of combat.enemies) {
    if (!ice.alive) continue;
    resolveIntent(run, ice, rng, events);
    drainHooks(run, rng, events); // hpLost relics (e.g. Counterspike) react
    if (phaseOf(combat) === "lost") {
      finalizeLoss(combat, events);
      return;
    }
  }

  // Tick enemy statuses (duration) at end of enemy phase.
  for (const ice of combat.enemies) {
    if (!ice.alive) continue;
    ice.statuses = tickEndOfTurnStatuses(ice.statuses);
  }

  // Hard countdown: tick once per enemy turn; once expired, the runner is found
  // and takes lethal-class unblockable pressure every turn thereafter.
  if (isCountdownActive(run.trace!)) {
    const res = tickHardCountdown(run.trace!);
    run.trace = res.trace;
    if (res.expired) {
      dealToRunner(run, TRACE_TUNING.countdownLethalDamage, true, events);
      checkCombatEnd(run, events);
      if (phaseOf(combat) === "lost") {
        finalizeLoss(combat, events);
        return;
      }
    }
  } else if (run.trace!.hardCountdown === 0) {
    dealToRunner(run, TRACE_TUNING.countdownLethalDamage, true, events);
    checkCombatEnd(run, events);
    if (phaseOf(combat) === "lost") {
      finalizeLoss(combat, events);
      return;
    }
  }

  // Choose next intents for survivors. (Block was already cleared at the start
  // of this enemy phase; block from a `defend` intent now persists through the
  // upcoming player turn.)
  for (const ice of combat.enemies) {
    if (!ice.alive) continue;
    chooseIntent(ice, combat, run.trace!, rng);
  }

  if (combat.phase === "enemy") {
    beginPlayerTurn(run, rng, events);
  }
}

function finalizeLoss(combat: CombatState, events: CombatEvent[]): void {
  combat.phase = "lost";
  if (!events.some((e) => e.t === "lose")) events.push({ t: "lose" });
}

// ---------------------------------------------------------------------------
// Card play
// ---------------------------------------------------------------------------

/**
 * Play a card from hand. `handIndex` indexes combat.hand; `targetUid` is the
 * chosen enemy uid (required for single-enemy-target cards). Mutates run.
 * Validation errors are thrown (api layer guards / surfaces them).
 */
export function playCard(
  run: RunState,
  handIndex: number,
  targetUid: number | undefined,
  rng: Rng,
  events: CombatEvent[],
): void {
  const combat = run.combat!;
  if (combat.phase !== "player") {
    throw new Error("Cannot play a card outside the player phase");
  }
  const card = combat.hand[handIndex];
  if (!card) throw new Error(`No card at hand index ${handIndex}`);
  const def = getCardDef(card.defId);

  if (def.unplayable) throw new Error(`Card ${def.id} is unplayable`);
  if (def.cost > combat.cycles) {
    throw new Error(
      `Not enough Cycles: need ${def.cost}, have ${combat.cycles}`,
    );
  }

  // Resolve + validate target.
  let target: IceInstance | undefined;
  if (def.target === "enemy") {
    if (targetUid == null) throw new Error(`Card ${def.id} needs a target`);
    target = combat.enemies.find((e) => e.uid === targetUid && e.alive);
    if (!target) throw new Error(`Invalid/dead target ${targetUid}`);
  }

  // Pay cost + remove from hand.
  combat.cycles -= def.cost;
  combat.hand.splice(handIndex, 1);

  events.push({
    t: "cardPlayed",
    uid: card.uid,
    defId: def.id,
    ...(target ? { targetUid: target.uid } : {}),
  });
  combat.cardsPlayedThisTurn += 1;
  if (def.type === "attack") combat.attacksPlayedThisTurn += 1;
  run.stats.cardsPlayed += 1;

  // Trace tax for playing a loud card.
  if (def.traceOnPlay) {
    pushTrace(run, def.traceOnPlay, `play:${def.id}`, events);
  }

  // Apply effects in order.
  for (const eff of def.effects) {
    applyEffect(run, eff, target, rng, events);
  }

  // Program card-played hooks: generic, then type-specific (attack/skill/power).
  const ctx: HookContext = { cardType: def.type, cardTags: def.tags ?? [] };
  if (target) ctx.target = target;
  fireRunnerHook(run, "cardPlayed", ctx, rng, events);
  if (def.type === "attack") fireRunnerHook(run, "attackPlayed", ctx, rng, events);
  else if (def.type === "skill") fireRunnerHook(run, "skillPlayed", ctx, rng, events);
  else if (def.type === "power") fireRunnerHook(run, "powerPlayed", ctx, rng, events);

  // Drain deferred hooks (kills/hpLost/traceGained) produced by this play.
  drainHooks(run, rng, events);

  // Route the card to discard or exhaust.
  if (def.exhaust) {
    combat.exhaustPile.push(card);
  } else {
    combat.discardPile.push(card);
  }

  checkCombatEnd(run, events);
}

function applyEffect(
  run: RunState,
  eff: CardEffect,
  target: IceInstance | undefined,
  rng: Rng,
  events: CombatEvent[],
): void {
  const combat = run.combat!;
  const runnerStat = runnerStatuses(run);

  // Resolve a scaled value if requested:
  //  - scaleByStatus: + current stacks of a status on the runner
  //  - scaleByCounter: + a per-turn combat counter (cards/attacks played).
  // Counters are read BEFORE this card incremented them (combat increments
  // cardsPlayedThisTurn after playCard's effect loop? — no, before; see
  // playCard: it bumps counters then applies effects. So the just-played card
  // IS counted. We subtract 1 so "+1 per OTHER card this turn" reads naturally.)
  let scale = 0;
  if (eff.scaleByStatus) scale += getStacks(runnerStat, eff.scaleByStatus);
  if (eff.scaleByCounter === "cardsPlayed") {
    scale += Math.max(0, combat.cardsPlayedThisTurn - 1);
  } else if (eff.scaleByCounter === "attacksPlayed") {
    scale += Math.max(0, combat.attacksPlayedThisTurn - 1);
  }

  switch (eff.kind) {
    case "damage": {
      if (!target) break;
      const base = (eff.value ?? 0) + scale;
      const out = computeOutgoingDamage(base, runnerStat);
      dealToIce(run, target, out, events);
      fireRunnerHook(run, "damageDealt", { amount: out, target }, rng, events);
      break;
    }
    case "damageMulti": {
      if (!target) break;
      const hits = eff.hits ?? 1;
      const base = (eff.value ?? 0) + scale;
      const out = computeOutgoingDamage(base, runnerStat);
      for (let i = 0; i < hits; i++) {
        if (!target.alive) break;
        dealToIce(run, target, out, events);
        fireRunnerHook(run, "damageDealt", { amount: out, target }, rng, events);
      }
      break;
    }
    case "damageAll": {
      const base = (eff.value ?? 0) + scale;
      const out = computeOutgoingDamage(base, runnerStat);
      for (const ice of combat.enemies) {
        if (ice.alive) {
          dealToIce(run, ice, out, events);
          fireRunnerHook(run, "damageDealt", { amount: out, target: ice }, rng, events);
        }
      }
      break;
    }
    case "block": {
      const dex = getStacks(runnerStat, "dexterity");
      const amount = (eff.value ?? 0) + scale + dex;
      run._runnerStatuses = addStatus(run._runnerStatuses, "block", amount);
      events.push({ t: "block", targetUid: RUNNER_UID, amount });
      fireRunnerHook(run, "blockGained", { amount }, rng, events);
      break;
    }
    case "applyStatus": {
      if (!target) break;
      const amt = (eff.amount ?? 0) + scale;
      target.statuses = addStatus(target.statuses, eff.status!, amt);
      events.push({
        t: "status",
        targetUid: target.uid,
        status: eff.status!,
        amount: amt,
      });
      break;
    }
    case "applyStatusAll": {
      const amt = (eff.amount ?? 0) + scale;
      for (const ice of combat.enemies) {
        if (!ice.alive) continue;
        ice.statuses = addStatus(ice.statuses, eff.status!, amt);
        events.push({
          t: "status",
          targetUid: ice.uid,
          status: eff.status!,
          amount: amt,
        });
      }
      break;
    }
    case "applyStatusSelf": {
      const amt = (eff.amount ?? 0) + scale;
      run._runnerStatuses = addStatus(run._runnerStatuses, eff.status!, amt);
      events.push({
        t: "status",
        targetUid: RUNNER_UID,
        status: eff.status!,
        amount: amt,
      });
      break;
    }
    case "spreadStatus": {
      if (!target || !eff.status) break;
      const stacks = getStacks(target.statuses, eff.status);
      if (stacks > 0) {
        for (const ice of combat.enemies) {
          if (!ice.alive || ice.uid === target.uid) continue;
          ice.statuses = addStatus(ice.statuses, eff.status, stacks);
          events.push({
            t: "status",
            targetUid: ice.uid,
            status: eff.status,
            amount: stacks,
          });
        }
      }
      break;
    }
    case "detonateStatus": {
      if (!target || !eff.status) break;
      const stacks = getStacks(target.statuses, eff.status);
      if (stacks > 0) {
        target.statuses = setStatus(target.statuses, eff.status, 0);
        // Detonation is raw, status-driven damage (ignores strength/weak).
        dealToIce(run, target, stacks, events);
        fireRunnerHook(run, "damageDealt", { amount: stacks, target }, rng, events);
      }
      break;
    }
    case "loseBlockToDamage": {
      if (!target) break;
      const blk = getStacks(run._runnerStatuses, "block");
      if (blk > 0) {
        const out = computeOutgoingDamage(blk, runnerStat);
        dealToIce(run, target, out, events);
        fireRunnerHook(run, "damageDealt", { amount: out, target }, rng, events);
      }
      break;
    }
    case "draw": {
      drawCards(combat, eff.value ?? 0, rng, events);
      break;
    }
    case "gainCycles": {
      combat.cycles += eff.value ?? 0;
      break;
    }
    case "loseHpSelf": {
      dealToRunner(run, eff.value ?? 0, true, events);
      break;
    }
    case "heal": {
      run.hp = Math.min(run.maxHp, run.hp + (eff.value ?? 0));
      break;
    }
    case "addTrace": {
      pushTrace(run, eff.value ?? 0, "card", events);
      break;
    }
    case "reduceTrace": {
      pushTrace(run, -(eff.value ?? 0), "card", events);
      break;
    }
    case "exhaustHand": {
      const moved = combat.hand.slice();
      combat.hand = [];
      for (const c of moved) combat.exhaustPile.push(c);
      break;
    }
    case "gainCardTemp": {
      if (eff.cardDefId) {
        const copies = eff.count ?? 1;
        for (let i = 0; i < copies; i++) {
          combat.hand.push({ uid: run.nextUid++, defId: eff.cardDefId });
        }
      }
      break;
    }
    default: {
      const _exhaustive: never = eff.kind;
      throw new Error(`Unhandled effect kind ${_exhaustive}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Damage application
// ---------------------------------------------------------------------------

/** Deal damage to an ICE: status math, then block, then HP. */
export function dealToIce(
  run: RunState,
  ice: IceInstance,
  raw: number,
  events: CombatEvent[],
): void {
  if (!ice.alive) return;
  const incoming = computeIncomingDamage(raw, ice.statuses);
  let blocked = 0;
  let remaining = incoming;
  if (ice.block > 0) {
    blocked = Math.min(ice.block, remaining);
    ice.block -= blocked;
    remaining -= blocked;
  }
  ice.hp -= remaining;
  run.stats.damageDealt += remaining;
  events.push({
    t: "damage",
    targetUid: ice.uid,
    amount: remaining,
    blocked,
  });
  if (ice.hp <= 0) {
    ice.hp = 0;
    ice.alive = false;
    events.push({ t: "iceDown", uid: ice.uid });
    run.stats.enemiesKilled += 1;
    if (getIceDef(ice.defId).isElite) run.stats.elitesKilled += 1;
    enqueueHook(run, { hook: "enemyKilled", targetUid: ice.uid });
  }
}

/**
 * Deal damage to the runner. If `unblockable`, ignores block + most mitigation
 * (still respects intangible cap unless lethal-class? No: countdown lethal is
 * truly unblockable). Updates HP + stats, sets phase lost if HP hits 0.
 */
export function dealToRunner(
  run: RunState,
  raw: number,
  unblockable: boolean,
  events: CombatEvent[],
): void {
  const combat = run.combat!;
  const runnerStat = runnerStatuses(run);
  let dmg: number;
  let blocked = 0;
  if (unblockable) {
    dmg = raw;
  } else {
    dmg = computeIncomingDamage(raw, runnerStat);
    const block = getStacks(runnerStat, "block");
    if (block > 0) {
      blocked = Math.min(block, dmg);
      run._runnerStatuses = setStatus(
        run._runnerStatuses,
        "block",
        block - blocked,
      );
      dmg -= blocked;
    }
  }
  run.hp -= dmg;
  run.stats.damageTaken += dmg;
  events.push({
    t: "damage",
    targetUid: RUNNER_UID,
    amount: dmg,
    blocked,
  });
  if (dmg > 0) enqueueHook(run, { hook: "hpLost", amount: dmg });
  if (run.hp <= 0) {
    run.hp = 0;
    combat.phase = "lost";
  }
}

// ---------------------------------------------------------------------------
// Enemy AI
// ---------------------------------------------------------------------------

/** Decide and set an ICE's next telegraphed intent (deterministic). */
export function chooseIntent(
  ice: IceInstance,
  combat: CombatState,
  trace: TraceState,
  rng: Rng,
): void {
  const def = getIceDef(ice.defId);
  let intent: Intent;
  if (def.ai.type === "loop") {
    const moves = def.ai.moves;
    const idx = ice.aiCursor % moves.length;
    intent = moves[idx]!.intent;
    ice.aiCursor = (ice.aiCursor + 1) % moves.length;
  } else if (def.ai.type === "weighted") {
    const moves = def.ai.moves;
    const weights = moves.map((m) => m.weight ?? 1);
    const chosen = rng.weightedPick(moves, weights);
    intent = chosen.intent;
  } else {
    // scripted
    intent = def.ai.script(combat.turn + 1, trace.value);
  }
  ice.intent = intent;
}

/** Resolve a single ICE's telegraphed intent against the runner / itself. */
export function resolveIntent(
  run: RunState,
  ice: IceInstance,
  rng: Rng,
  events: CombatEvent[],
): void {
  const combat = run.combat!;
  const intent = ice.intent;
  events.push({ t: "intent", uid: ice.uid, intent });

  const attackerStat = ice.statuses;

  switch (intent.kind) {
    case "attack": {
      const hits = intent.hits ?? 1;
      for (let i = 0; i < hits; i++) {
        const out = computeOutgoingDamage(intent.value ?? 0, attackerStat);
        dealToRunner(run, out, false, events);
        if (combat.phase === "lost") return;
      }
      break;
    }
    case "attackDebuff": {
      const hits = intent.hits ?? 1;
      for (let i = 0; i < hits; i++) {
        const out = computeOutgoingDamage(intent.value ?? 0, attackerStat);
        dealToRunner(run, out, false, events);
        if (combat.phase === "lost") return;
      }
      if (intent.status) {
        run._runnerStatuses = addStatus(
          run._runnerStatuses,
          intent.status,
          intent.amount ?? 0,
        );
        events.push({
          t: "status",
          targetUid: RUNNER_UID,
          status: intent.status,
          amount: intent.amount ?? 0,
        });
      }
      break;
    }
    case "defend": {
      ice.block += intent.value ?? 0;
      events.push({ t: "block", targetUid: ice.uid, amount: intent.value ?? 0 });
      break;
    }
    case "buff": {
      if (intent.status) {
        ice.statuses = addStatus(ice.statuses, intent.status, intent.amount ?? 0);
        events.push({
          t: "status",
          targetUid: ice.uid,
          status: intent.status,
          amount: intent.amount ?? 0,
        });
      }
      break;
    }
    case "debuff": {
      if (intent.status) {
        run._runnerStatuses = addStatus(
          run._runnerStatuses,
          intent.status,
          intent.amount ?? 0,
        );
        events.push({
          t: "status",
          targetUid: RUNNER_UID,
          status: intent.status,
          amount: intent.amount ?? 0,
        });
      }
      break;
    }
    case "summon": {
      if (intent.summonDefId) {
        const minion = makeIce(intent.summonDefId, rng, () => run.nextUid++);
        chooseIntent(minion, combat, run.trace!, rng);
        combat.enemies.push(minion);
      }
      break;
    }
    case "trace": {
      pushTrace(run, intent.traceAmount ?? 0, `ice:${ice.defId}`, events);
      break;
    }
    case "unknown":
      break;
    default: {
      const _exhaustive: never = intent.kind;
      throw new Error(`Unhandled intent kind ${_exhaustive}`);
    }
  }
}

// ---------------------------------------------------------------------------
// Start-of-turn status ticks (DoT / regen)
// ---------------------------------------------------------------------------

/**
 * Apply start-of-turn intensity effects (virus, poison, regen) for an actor.
 * For the runner pass isRunner=true; for an ICE pass the ice. Mutates the
 * relevant status map + HP and emits damage events.
 */
function applyStartOfTurnStatusTicks(
  run: RunState,
  statuses: StatusMap,
  isRunner: boolean,
  ice: IceInstance | undefined,
  events: CombatEvent[],
): void {
  let s = statuses;

  // Virus: deal `stacks` damage, then halve (rounded down).
  const virus = getStacks(s, "virus");
  if (virus > 0) {
    if (isRunner) dealToRunner(run, virus, true, events);
    else if (ice) dealToIce(run, ice, virus, events);
    s = setStatus(s, "virus", Math.floor(virus / 2));
  }

  // Poison: deal `stacks` damage, then -1.
  const poison = getStacks(s, "poison");
  if (poison > 0) {
    if (isRunner) dealToRunner(run, poison, true, events);
    else if (ice) dealToIce(run, ice, poison, events);
    s = addStatus(s, "poison", -1);
  }

  // Regen: heal `stacks`, then -1.
  const regen = getStacks(s, "regen");
  if (regen > 0) {
    if (isRunner) run.hp = Math.min(run.maxHp, run.hp + regen);
    else if (ice) ice.hp = Math.min(ice.maxHp, ice.hp + regen);
    s = addStatus(s, "regen", -1);
  }

  if (isRunner) run._runnerStatuses = s;
  else if (ice) ice.statuses = s;
}

// ---------------------------------------------------------------------------
// Trace plumbing
// ---------------------------------------------------------------------------

function pushTrace(
  run: RunState,
  delta: number,
  reason: string,
  events: CombatEvent[],
): void {
  const before = run.trace!.value;
  const res = addTrace(run.trace!, delta, reason);
  run.trace = res.trace;
  for (const ev of res.events) events.push(ev);
  if (res.trace.value > run.stats.maxTraceReached) {
    run.stats.maxTraceReached = res.trace.value;
  }
  // Apply escalation effects for any tier events just produced.
  for (const ev of res.events) {
    if (ev.t === "traceEscalate") applyTraceEscalation(run, ev.tier);
  }
  // Queue the traceGained Program hook only on a genuine increase, and never
  // for program-sourced trace (reason "program") so trace-reactive relics can't
  // self-trigger into a loop.
  const gained = res.trace.value - before;
  if (gained > 0 && reason !== "program") {
    enqueueHook(run, { hook: "traceGained", amount: gained });
  }
}

/**
 * Escalation: each tier strengthens the threat. Tier 0: all ICE +1 strength.
 * Tier 1: all ICE +2 strength. Tier 2 (max threshold): a heavy global pressure
 * (all ICE +2 strength again). Deterministic, no RNG.
 */
function applyTraceEscalation(run: RunState, tier: number): void {
  const combat = run.combat;
  if (!combat) return;
  const strengthByTier = [1, 2, 2];
  const amt = strengthByTier[tier] ?? 1;
  for (const ice of combat.enemies) {
    if (ice.alive) {
      ice.statuses = addStatus(ice.statuses, "strength", amt);
    }
  }
}

// ---------------------------------------------------------------------------
// Win / lose
// ---------------------------------------------------------------------------

export function checkCombatEnd(run: RunState, events: CombatEvent[]): void {
  const combat = run.combat!;
  if (combat.phase === "won" || combat.phase === "lost") return;
  if (run.hp <= 0) {
    combat.phase = "lost";
    events.push({ t: "lose" });
    return;
  }
  const anyAlive = combat.enemies.some((e) => e.alive);
  if (!anyAlive) {
    combat.phase = "won";
    events.push({ t: "win" });
    run.stats.combatsWon += 1;
  }
}

// ---------------------------------------------------------------------------
// Runner status helpers
// ---------------------------------------------------------------------------

/**
 * The runner's statuses + block live on the RunState under a private field so
 * combat math can treat the runner like any other actor. We attach it lazily.
 */
export const RUNNER_UID = -1;

export function runnerStatuses(run: RunState): StatusMap {
  if (!run._runnerStatuses) run._runnerStatuses = {};
  return run._runnerStatuses;
}

/** Public read: runner's current block. */
export function getRunnerBlock(run: RunState): number {
  return getStacks(run._runnerStatuses, "block");
}

// Runner status processing for start-of-turn was hooked in beginPlayerTurn,
// but ICE DoT must run when? ICE take virus/poison at the START of the enemy
// phase (i.e. when endTurn resolves). We tick ICE DoT at the top of endTurn.
export function applyEnemyStartOfTurnDoT(
  run: RunState,
  events: CombatEvent[],
): void {
  const combat = run.combat!;
  for (const ice of combat.enemies) {
    if (!ice.alive) continue;
    applyStartOfTurnStatusTicks(run, ice.statuses, false, ice, events);
  }
}
